/**
 * Signup — /signup route.
 *
 * Mirrors /login (see login.tsx for the full flow shape):
 *  - OIDC mode: needs ?authRequest; if absent, fire signinRedirect
 *    once so Zitadel mints one and 302s back.
 *  - Stub mode: form submits to the synthetic stub signUp which
 *    flips to STUB_USER + navigates to /app.
 *
 * Signup creates the Zitadel human user with isVerified:true (v1
 * trade-off — see ADR; SMTP isn't wired so we can't send a real
 * verification mail yet) and immediately signs the new user in.
 * Google/Apple federation buttons render when their IdP ids are
 * configured (see lib/idp-login.ts) — same mechanism as /login.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { authErrorMessage } from "@/lib/auth-errors";
import { configuredIdps, startIdpLogin } from "@/lib/idp-login";
import { isOidcConfigured } from "@/lib/oidc-config";
import { useTenantTheme } from "@/lib/tenant-theme";
import { useToast } from "@/lib/ui/toast";
import { BrandMark } from "@/lib/ui/brand-mark";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { Label } from "@/lib/ui/label";
import { Separator } from "@/lib/ui/separator";

const HIGHLIGHTS = [
  "Foundry pack ready to enable in one click",
  "Bring your own gateway hardware, or order a SAHAS appliance",
  "Free for one gateway and 50 tags — upgrade only when you scale",
];

/**
 * Split a single "name" input into Zitadel-shaped given/family.
 * Whitespace-split on the first run; single-word names get the same
 * value for both fields (Zitadel rejects empty familyName).
 */
function splitName(full: string): { givenName: string; familyName: string } {
  const trimmed = full.trim();
  if (trimmed === "") return { givenName: "", familyName: "" };
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { givenName: parts[0], familyName: parts[0] };
  }
  const given = parts[0];
  const family = parts.slice(1).join(" ");
  return { givenName: given, familyName: family };
}

export function Signup() {
  const { signUp, initiateOidcRedirect, isAuthenticated, isLoading } =
    useAuth();
  const tenant = useTenantTheme();
  const navigate = useNavigate();
  const { push: pushToast } = useToast();
  const [searchParams] = useSearchParams();
  const authRequestId = searchParams.get("authRequest");
  const oidc = isOidcConfigured();
  const [submitting, setSubmitting] = useState(false);
  const redirectFiredRef = useRef(false);

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      navigate("/app", { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!oidc) return;
    if (isLoading || isAuthenticated) return;
    if (authRequestId) return;
    if (redirectFiredRef.current) return;
    redirectFiredRef.current = true;
    initiateOidcRedirect();
  }, [oidc, isLoading, isAuthenticated, authRequestId, initiateOidcRedirect]);

  if (oidc && !authRequestId) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface px-6 py-12 text-on-surface">
        <BrandMark variant="full" className="h-12" />
        <p className="text-sm text-on-surface-muted">
          {isAuthenticated
            ? "Already signed in — taking you home…"
            : isLoading
              ? "Loading session…"
              : "Preparing sign-up…"}
        </p>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    const fd = new FormData(e.currentTarget);
    const fullName = String(fd.get("name") ?? "").trim();
    const email = String(fd.get("email") ?? "").trim();
    const password = String(fd.get("password") ?? "");
    if (!fullName || !email || !password) {
      pushToast("error", "Name, email and password are required.");
      return;
    }
    const { givenName, familyName } = splitName(fullName);

    setSubmitting(true);
    const result = await signUp({
      email,
      password,
      givenName,
      familyName,
      authRequestId: authRequestId ?? "stub",
    });
    setSubmitting(false);

    if (result.ok) {
      window.location.assign(result.callbackUrl);
      return;
    }
    pushToast("error", authErrorMessage(result.code));
  };

  return (
    <div className="grid min-h-screen bg-surface lg:grid-cols-2">
      <aside className="relative hidden overflow-hidden bg-brand text-brand-foreground lg:flex lg:flex-col lg:justify-between lg:p-12">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-32 -top-32 size-[420px] rounded-full bg-brand-foreground/10 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-40 -left-20 size-[520px] rounded-full bg-brand-foreground/5 blur-3xl"
        />

        <Link
          to="/"
          className="relative flex items-center"
          aria-label={tenant.productName}
        >
          {/* On the brand-coloured hero the dark lockup is always inverted to
              read light, independent of light/dark theme mode. */}
          <BrandMark variant="full" className="h-10 invert dark:invert-0" />
        </Link>

        <div className="relative flex flex-col gap-5">
          <Sparkles className="size-6 opacity-70" />
          <p className="max-w-md text-2xl font-semibold leading-snug">
            Start with a foundry — keep what works, replace the spreadsheets.
          </p>
          <ul className="flex flex-col gap-2 text-sm text-brand-foreground/85">
            {HIGHLIGHTS.map((h) => (
              <li key={h} className="flex items-start gap-2">
                <Check className="mt-0.5 size-4 shrink-0 opacity-90" />
                <span>{h}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-brand-foreground/70">
          One platform. Two tiers. Same login.
        </p>
      </aside>

      <main className="flex flex-col">
        <div className="flex items-center justify-between border-b border-border px-6 py-4 lg:hidden">
          <Link to="/" className="flex items-center" aria-label={tenant.productName}>
            <BrandMark variant="full" className="h-9" />
          </Link>
        </div>

        <div className="flex flex-1 items-center justify-center p-6 md:p-12">
          <div className="flex w-full max-w-sm flex-col gap-6">
            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-semibold tracking-tight text-on-surface">
                Create your account
              </h1>
              <p className="text-sm text-on-surface-muted">
                Free for one gateway, 50 tags, 7-day retention. No card needed.
              </p>
            </div>

            {authRequestId && configuredIdps().length > 0 && (
              <>
                <div className="flex flex-col gap-2">
                  {configuredIdps().map((p) => (
                    <Button
                      key={p.id}
                      variant="outline"
                      type="button"
                      onClick={() => {
                        void startIdpLogin(p.id, authRequestId).catch(() =>
                          pushToast(
                            "error",
                            "Couldn't start sign-in with that provider. Please try again.",
                          ),
                        );
                      }}
                    >
                      {p.label}
                    </Button>
                  ))}
                </div>

                <div className="flex items-center gap-3 text-[11px] uppercase tracking-wider text-on-surface-muted">
                  <Separator className="flex-1" />
                  or with email
                  <Separator className="flex-1" />
                </div>
              </>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="name">Your name</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  disabled={submitting}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="email">Work email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  required
                  disabled={submitting}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  disabled={submitting}
                />
                <p className="text-xs text-on-surface-muted">
                  At least 8 characters with upper- &amp; lower-case letters, a
                  number, and a symbol.
                </p>
              </div>

              <Button type="submit" size="lg" disabled={submitting}>
                {submitting ? "Creating account…" : "Create account"}
                {!submitting && <ArrowRight className="ml-1.5 size-4" />}
              </Button>
            </form>

            <p className="text-center text-sm text-on-surface-muted">
              Already have an account?{" "}
              <Link
                to={authRequestId ? `/login?authRequest=${authRequestId}` : "/login"}
                className="font-medium text-brand hover:underline"
              >
                Sign in
              </Link>
            </p>

            <p className="text-center text-xs text-on-surface-muted">
              By signing up you agree to the{" "}
              <Link to="/terms" className="underline hover:text-on-surface">
                Terms
              </Link>{" "}
              and{" "}
              <Link to="/privacy" className="underline hover:text-on-surface">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
